/**
 * Opens the unified tenant profile (the same modal the Tenants list opens)
 * from a support ticket — Neel's V2.3 item 6.
 *
 * A ticket knows a *user*, not a booking, but the profile is booking-scoped:
 * every tab (payments, move-in, move-out, documents) hangs off one booking
 * row. `pickTenantBooking` is that resolution, and it is the only decision in
 * this file worth testing on its own.
 *
 * The rest mirrors pages/admin/v2/TenantDetail.tsx deliberately — same
 * TenantProfileTenant assembly, same contract callbacks — so "open the
 * tenant profile" means the same thing from a ticket as it does from a room.
 * The two reads that find the tenant's booking and profile are the exception:
 * they are `useTicketTenant`'s, not `useBookingsForUser` / `useProfile`, whose
 * select lists name columns that do not exist (`rooms.room_number`,
 * `profiles.avatar_url`) and so always fail, which the launcher mistook for
 * "has no booking" (see useTicketTenant.ts).
 * Neel asked for "the normal tenant profile with all its functionalities",
 * which is exactly the contract-generate / send pair being wired here rather
 * than left undefined (the dialog hides those buttons when they are absent).
 *
 * The profile is booking-scoped, but a ticket's tenant is not always a booker
 * (an applicant who only emailed in). Without a booking the click used to
 * render nothing at all; it now opens a small contact popup instead
 * (`TenantContactDialog`), so the name always answers.
 */
import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  isActiveBooking,
  useProperty,
  useRoom,
  type BookingRow,
} from "@/hooks/v2/useProperties";
import {
  useTicketTenantBookings,
  useTicketTenantProfile,
} from "./useTicketTenant";
import {
  TenantProfileDialog,
  type TenantProfileTenant,
} from "@/components/admin/v2/tenant/TenantProfileDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  invokeCreateContractForBooking,
  invokeSendContractToTenant,
} from "@/lib/adminCreateContract";
import { supabase } from "@/lib/supabase";

type ProfilePayment = {
  id: string;
  amount_cents: number | null;
  status: string | null;
  due_date: string | null;
  paid_at: string | null;
};

type ProfileCase = {
  id: string;
  title: string;
  created_at: string;
};

/**
 * Which booking the profile opens on. Neel: "open the current one or most
 * advanced one is good."
 *
 * The current one = the active booking, using the app-wide occupancy
 * predicate rather than a local guess, so the ticket agrees with the room's
 * Occupied badge. With no active booking (applicant, or a past tenant still
 * writing in) it falls back to the newest row — `useTicketTenantBookings`
 * orders by created_at descending, so that is simply the first.
 */
export function pickTenantBooking(
  bookings: BookingRow[] | undefined
): BookingRow | null {
  if (!bookings?.length) return null;
  return bookings.find(isActiveBooking) ?? bookings[0];
}

export function TenantProfileLauncher({
  userId,
  tenantName = null,
  tenantEmail = null,
  ticketId = null,
  compact = false,
  open,
  onOpenChange,
}: {
  /** The ticket's `reported_by`. Null on an unowned ticket — nothing opens. */
  userId: string | null;
  /** The name the ticket header already shows; titles the popup until the
   *  profile row arrives so the click answers immediately. */
  tenantName?: string | null;
  /** The email the ticket header already shows; stands in for the profile's
   *  own when that read is missing or failed, so the popup is never blank
   *  about something the ticket plainly knows. */
  tenantEmail?: string | null;
  ticketId?: string | null;
  /** Use the compact support drawer from the supplied inbox mockup. */
  compact?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { isSuperAdmin, canManageTenant, user: currentUser } = useAuth();
  const [fullProfileOpen, setFullProfileOpen] = useState(false);
  // Every query below is gated on `open`: a closed launcher sits on every
  // ticket in the inbox and must not fetch a booking history per row.
  const {
    data: bookings,
    // isFetching, not isLoading: on a reopen the cached (possibly empty) answer
    // is already there while a refetch is still in flight, and an empty list that
    // is being re-read is not yet a fact worth stating.
    isFetching: bookingsFetching,
    isError: bookingsFailed,
  } = useTicketTenantBookings(open ? userId : null);
  const booking = pickTenantBooking(bookings);
  // Keyed on the ticket's user, not `booking.user_id`: the bookings above were
  // fetched by that same user_id, and a tenant with no booking still has a
  // profile to show (and this no longer waits behind the bookings query).
  const { data: profile, isLoading: profileLoading } = useTicketTenantProfile(
    open ? userId : null
  );
  const { data: room } = useRoom(open ? booking?.room_id : null);
  const { data: property } = useProperty(
    open ? (room as Record<string, any> | null)?.property_id : null
  );

  const { data: recentPayments = [] } = useQuery({
    queryKey: ["support-profile-payments", booking?.id],
    enabled: compact && open && !!booking?.id,
    queryFn: async (): Promise<ProfilePayment[]> => {
      const { data, error } = await supabase
        .from("payments")
        .select("id, amount_cents, status, due_date, paid_at")
        .eq("booking_id", booking!.id)
        .eq("type", "rent")
        .order("due_date", { ascending: false })
        .limit(3);
      if (error) throw error;
      return (data ?? []) as ProfilePayment[];
    },
  });

  const { data: previousCases = [] } = useQuery({
    queryKey: ["support-profile-cases", userId, ticketId],
    enabled: compact && open && !!userId,
    queryFn: async (): Promise<ProfileCase[]> => {
      let query = supabase
        .from("issues")
        .select("id, title, created_at")
        .or(`reported_by.eq.${userId},user_id.eq.${userId}`)
        .order("created_at", { ascending: false })
        .limit(4);
      if (ticketId) query = query.neq("id", ticketId);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).slice(0, 3) as ProfileCase[];
    },
  });

  if (!open || !userId) return null;

  const p = profile as Record<string, any> | null | undefined;

  // No booking to hang the full profile on — an applicant who has only
  // written in, or a bookings read that is still in flight or failed. The
  // click must still answer, so a small contact popup stands in rather than
  // the launcher rendering nothing.
  if (!booking) {
    return (
      <TenantContactDialog
        name={
          p?.full_name?.trim() ||
          p?.name?.trim() ||
          tenantName?.trim() ||
          p?.email?.trim() ||
          "—"
        }
        email={p?.email ?? tenantEmail}
        phone={p?.phone ?? null}
        // Still "loading" until the profile has landed too, so the dashes for
        // not-yet-known contact details never sit beside a settled status.
        status={
          bookingsFetching || profileLoading
            ? "loading"
            : bookingsFailed
              ? "failed"
              : "empty"
        }
        onOpenChange={onOpenChange}
      />
    );
  }

  const tenant: TenantProfileTenant = {
    id: booking.id,
    userId: booking.user_id ?? null,
    booking,
    profile: p
      ? {
          id: p.id,
          name: p.full_name ?? p.name ?? null,
          full_name: p.full_name ?? null,
          email: p.email ?? null,
          phone: p.phone ?? null,
          avatar_url: p.avatar_url ?? null,
          date_of_birth: p.date_of_birth ?? null,
        }
      : // No profile row yet (still loading, or the read failed): hand the
        // dialog what the ticket header already shows, so it never opens
        // nameless. Its own live profile merges over this once it arrives.
        {
          id: userId,
          name: tenantName,
          full_name: null,
          email: tenantEmail,
          phone: null,
          avatar_url: null,
          date_of_birth: null,
        },
    room: room ?? null,
    property: property ?? null,
  };

  if (compact) {
    return (
      <>
        <SupportTenantProfileDrawer
          open={open && !fullProfileOpen}
          onOpenChange={(next) => {
            if (!next) {
              setFullProfileOpen(false);
              onOpenChange(false);
            }
          }}
          name={
            p?.full_name?.trim() ||
            p?.name?.trim() ||
            tenantName?.trim() ||
            p?.email?.trim() ||
            "—"
          }
          propertyTitle={(property as Record<string, any> | null)?.title ?? "—"}
          roomTitle={(room as Record<string, any> | null)?.title ?? "—"}
          monthlyRent={booking.monthly_rent}
          payments={recentPayments}
          previousCases={previousCases}
          onOpenFullProfile={() => setFullProfileOpen(true)}
        />
        <TenantProfileDialog
          tenant={tenant}
          open={fullProfileOpen}
          onOpenChange={setFullProfileOpen}
          isSuperAdmin={isSuperAdmin}
          canManageTenant={canManageTenant}
          currentUserId={currentUser?.id ?? null}
          onGenerateContract={(bid, opts) =>
            invokeCreateContractForBooking(bid, opts).then(() => undefined)
          }
          onSendContract={(bid) =>
            invokeSendContractToTenant(bid).then(() => undefined)
          }
        />
      </>
    );
  }

  return (
    <TenantProfileDialog
      tenant={tenant}
      open
      onOpenChange={onOpenChange}
      isSuperAdmin={isSuperAdmin}
      canManageTenant={canManageTenant}
      currentUserId={currentUser?.id ?? null}
      onGenerateContract={(bid, opts) =>
        invokeCreateContractForBooking(bid, opts).then(() => undefined)
      }
      onSendContract={(bid) =>
        invokeSendContractToTenant(bid).then(() => undefined)
      }
    />
  );
}

function SupportTenantProfileDrawer({
  open,
  onOpenChange,
  name,
  propertyTitle,
  roomTitle,
  monthlyRent,
  payments,
  previousCases,
  onOpenFullProfile,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  propertyTitle: string;
  roomTitle: string;
  monthlyRent: number | null;
  payments: ProfilePayment[];
  previousCases: ProfileCase[];
  onOpenFullProfile: () => void;
}) {
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const formatMoney = (cents: number | null, fallback?: number | null) => {
    const euros = cents == null ? Number(fallback ?? 0) : cents / 100;
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(euros);
  };
  const formatMonth = (value: string | null) => {
    if (!value) return "—";
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  };
  const paid = (status: string | null) =>
    ["paid", "succeeded", "completed"].includes(
      String(status ?? "").toLowerCase()
    );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay className="support-profile-overlay" />
      <DialogPrimitive.Content
        className="support-profile-drawer"
        aria-describedby={undefined}
      >
        <div className="support-profile-heading">
          <DialogPrimitive.Title>{name}</DialogPrimitive.Title>
          <DialogPrimitive.Close>
            {t("common.close", "Close")}
          </DialogPrimitive.Close>
        </div>
        <dl className="support-profile-facts">
          <dt>{t("support.new.property", "Property")}</dt>
          <dd>{propertyTitle}</dd>
          <dt>{t("support.new.room", "Room")}</dt>
          <dd>{roomTitle}</dd>
          <dt>{t("support.profile.rent", "Rent")}</dt>
          <dd>{formatMoney(null, monthlyRent)}</dd>
        </dl>

        <section className="support-profile-section">
          <h3>{t("support.profile.payments", "PAYMENTS")}</h3>
          {payments.length ? (
            payments.map((payment) => (
              <div className="support-profile-payment" key={payment.id}>
                <span>
                  {formatMonth(payment.due_date)} ·{" "}
                  {formatMoney(payment.amount_cents, monthlyRent)}
                </span>
                <strong className={paid(payment.status) ? "" : "is-open"}>
                  {paid(payment.status)
                    ? `${t("support.profile.paid", "paid")}${payment.paid_at ? ` ${new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" }).format(new Date(payment.paid_at))}.` : ""}`
                    : t("support.list.open", "open")}
                </strong>
              </div>
            ))
          ) : (
            <p className="support-profile-empty">
              {t("support.profile.noPayments", "No payments available.")}
            </p>
          )}
          <button
            type="button"
            className="support-profile-open-full"
            onClick={onOpenFullProfile}
          >
            {t("support.profile.openFull", "Open full tenant profile")}
          </button>
        </section>

        <section className="support-profile-section">
          <h3>{t("support.profile.previousCases", "PREVIOUS CASES")}</h3>
          {previousCases.length ? (
            previousCases.map((item) => (
              <article className="support-profile-case" key={item.id}>
                <strong>{item.title}</strong>
                <span>{formatMonth(item.created_at)}</span>
              </article>
            ))
          ) : (
            <p className="support-profile-empty">
              {t("support.profile.noPreviousCases", "No previous cases.")}
            </p>
          )}
        </section>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
}

/**
 * The popup for a ticket whose tenant has no booking to open the full profile
 * on. It is a plain dialog over the ticket — closing it only flips the
 * caller's `open` flag, so the admin lands back on the ticket, never on the
 * Tenants overview.
 *
 * `failed` deliberately says nothing about bookings: if the read errored we do
 * not know the tenant has none, and "No bookings yet." would be a false claim.
 */
function TenantContactDialog({
  name,
  email,
  phone,
  status,
  onOpenChange,
}: {
  name: string;
  email: string | null;
  phone: string | null;
  status: "loading" | "empty" | "failed";
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLanguage();
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[440px] rounded-2xl"
        // A failed read has no status line to describe the dialog; opt out
        // rather than leave Radix pointing at a description that isn't there.
        {...(status === "failed" ? { "aria-describedby": undefined } : {})}
      >
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{name}</DialogTitle>
          {status === "loading" ? (
            <DialogDescription className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {t("support.detail.loading", "Loading…")}
            </DialogDescription>
          ) : status === "empty" ? (
            <DialogDescription>
              {t("admin.tenants.emptyAll", "No bookings yet.")}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">
            {t("admin.tenantEdit.email", "Email")}
          </dt>
          <dd className="min-w-0 break-words text-foreground">
            {email || "—"}
          </dd>
          <dt className="text-muted-foreground">
            {t("admin.tenantEdit.phone", "Phone")}
          </dt>
          <dd className="min-w-0 break-words text-foreground">
            {phone || "—"}
          </dd>
        </dl>
      </DialogContent>
    </Dialog>
  );
}
